import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {useDateQueryParameter, useStringListQueryState, useStringQueryState} from "../../utils/useQueryState.js";
import moment from "moment-timezone";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {ProjectApi} from "../../api/authority/generated/index";

export const FilterContext = createContext(null);

export default function DashboardFilterContextProvider({children}) {

    let {context, getAuthorityConfiguration} = useContext(GlobalContext);

    let authorityConfiguration = useAuthorityConfiguration();

    // DateRange:
    let [since, setSince] = useDateQueryParameter('since', moment().startOf('month'));
    let [until, setUntil] = useDateQueryParameter('until', moment());

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Sources
    // -----------------------------------------------------------------------------------------------------------------
    let [sourcesPossible, setSourcesPossible] = useState([]);
    let [sourcesRemoved, setSourcesRemoved] = useStringListQueryState('sourcesRemoved', []);

    let sourceRegister = (source) => {
        setSourcesPossible((sourcesPossible) => {
            if (sourcesPossible.includes(source)) {
                return sourcesPossible;
            }

            return [...sourcesPossible, source];
        });
    };

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Campaigns
    // -----------------------------------------------------------------------------------------------------------------

    let [campaignSearchFilter, setCampaignSearchFilter] = useStringQueryState('campaignSearchFilter', '');
    let [campaignsAllowListed, setCampaignsAllowListed] = useStringListQueryState('campaignsAllowListed', []);

    let [campaignsPossible, setCampaignsPossible] = useState({});

    let campaignRegister = (campaign) => {
        setCampaignsPossible((campaignsPossible) => {

            let campaignKeys = Object.keys(campaignsPossible);

            if (campaignKeys.includes(campaign.remoteId)) {
                return campaignsPossible;
            }

            const campaigns = {...campaignsPossible};
            campaigns[campaign.remoteId] = campaign;
            return campaigns;
        });
    };
    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Centres & Domains
    // -----------------------------------------------------------------------------------------------------------------

    let [centresAllowListed, setCentresAllowListed] = useStringListQueryState('centresAllowListed', []);
    let [centresPossible, setCentresPossible] = useState([]);

    let [domainsAllowListed, setDomainsAllowListed] = useStringListQueryState('domainsAllowListed', []);
    let [domainsPossible, setDomainsPossible] = useState([]);

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Centres
    // -----------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (!context?.currentProjectId) {
            return;
        }

        new ProjectApi(authorityConfiguration)
            .listProjectCentres({
                projectId: context.currentProjectId
            })
            .then(setCentresPossible);
    }, [context.currentProjectId]);

    let centresPossibleReducedByCentresAllowListed = useMemo(() => {
        if ((domainsAllowListed ?? []).length === 0) {
            // No centre selected, all domains possible.
            return centresPossible;
        }

        let reducedCentres = [];

        for (let centrePossible of Object.values(centresPossible)) {
            for (let domainIdAllowListed of domainsAllowListed) {
                for (let centreDomain of centrePossible.domains) {
                    if (centreDomain.id === domainIdAllowListed) {
                        reducedCentres.push(centrePossible);
                    }
                }
            }
        }

        return reducedCentres;
    }, [domainsAllowListed, centresPossible]);

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Domains
    // -----------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (!context?.currentProjectId) {
            return;
        }

        new ProjectApi(authorityConfiguration)
            .listProjectDomains({
                projectId: context.currentProjectId
            })
            .then(setDomainsPossible);
    }, [context.currentProjectId]);

    useEffect(() => {
        setSourcesPossible([]);
        setCampaignsPossible([]);
    }, [domainsAllowListed, centresAllowListed]);

    let domainsPossibleReducedByCentresAllowListed = useMemo(() => {

        if ((centresAllowListed ?? []).length === 0) {
            // No centre selected, all domains possible.
            return domainsPossible;
        }

        let reducedDomains = [];

        for (let domainPossible of Object.values(domainsPossible)) {
            for (let centreIdAllowListed of centresAllowListed) {
                for (let domainCentre of domainPossible.centres) {
                    if (domainCentre.id === centreIdAllowListed) {
                        reducedDomains.push(domainPossible);
                    }
                }
            }
        }

        return reducedDomains;
    }, [centresAllowListed, domainsPossible]);

    return (
        <FilterContext.Provider value={{
            // Date
            since: since, setSince: setSince,
            until: until, setUntil: setUntil,

            // Sources
            sourcesPossible: sourcesPossible, setSourcesPossible: setSourcesPossible,
            sourcesRemoved: sourcesRemoved, setSourcesRemoved: setSourcesRemoved,
            sourceRegister: sourceRegister,

            // Campaigns
            campaignSearchFilter: campaignSearchFilter, setCampaignSearchFilter: setCampaignSearchFilter,
            campaignsAllowListed: campaignsAllowListed, setCampaignsAllowListed: setCampaignsAllowListed,
            campaignsPossible: campaignsPossible, setCampaignsPossible: setCampaignsPossible,
            campaignRegister: campaignRegister,

            // Centres
            centresPossible: centresPossibleReducedByCentresAllowListed, // centresPossible,
            centresAllowListed: centresAllowListed, setCentresAllowListed: setCentresAllowListed,

            // Domains
            domainsPossible: domainsPossibleReducedByCentresAllowListed, // domainsPossible,
            domainsAllowListed: domainsAllowListed, setDomainsAllowListed: setDomainsAllowListed,
        }}>
            {children}
        </FilterContext.Provider>
    );
}
